<template>
  <div class="container-fluid mt-5">
    <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
      <template slot="header">
        <div class="row" v-if="!!transfer.id">
          <div class="col-8">
            <h3 class="mb-0 view-header-buttons">
              {{ transfer.seq_number ?? transfer.code }}
              | {{ transfer.sourceEstablishment.company_name }} =>
              {{ transfer.destinationEstablishment.company_name }}
              <base-button
                class="mr-2"
                type="primary"
                size="sm"
                @click="goBack"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>
              <br/><br/>
              <base-button
                v-if="$currentUserCan($permissions.PERM_EDIT_ESTABLISHMENT_TRANSFERS) && transfer.status === 'DRAFT'"
                class="mr-2"
                type="primary"
                icon
                size="sm"
                :disabled="loading"
                @click="goToEdit"
              >
                <i class="fas fa-edit"></i>
                {{ $t("COMMON.EDIT") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'DRAFT' && transfer.items.length > 0"
                class="mr-2"
                type="primary"
                icon
                size="sm"
                :loading="loading"
                @click="validate"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t("COMMON.SUBMIT") }}
              </base-button>
              <base-button
                v-if="
                  transfer.status === 'DRAFT' &&
                  $currentUserCan($permissions.PERM_DELETE_ESTABLISHMENT_TRANSFERS)
                "
                class="mr-2"
                type="danger"
                icon
                size="sm"
                :loading="loading"
                @click="deleteTransfer"
              >
                <i class="fas fa-trash"></i>
                {{ $t("COMMON.DELETE") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'SUBMITTED' && !isSubmitter"
                class="mr-2"
                type="success"
                icon
                size="sm"
                :loading="loading"
                @click="accept"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t("ESTABLISHMENT_TRANSFERS.ACCEPT") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'SUBMITTED' && !isSubmitter"
                class="mr-2"
                type="warning"
                icon
                size="sm"
                :loading="loading"
                @click="reject"
              >
                <i class="ni ni-fat-remove"></i>
                {{ $t("ESTABLISHMENT_TRANSFERS.REJECT") }}
              </base-button>
              <base-button
                v-if="canCancel"
                type="danger"
                icon
                size="sm"
                :loading="loading"
                @click="cancel"
              >
                <i class="ni ni-fat-remove"></i>
                {{ $t("COMMON.CANCEL") }}
              </base-button>
              <base-button
                v-if="
                  transfer.status === 'SUBMITTED' ||
                  transfer.status === 'ACCEPTED'
                "
                class="mr-2"
                type="info"
                icon
                size="sm"
                :disabled="loading"
                @click="print"
              >
                <i class="fa fa-file-pdf"></i>
                {{ $t("COMMON.PRINT") }}
              </base-button>
              <base-button
                v-if="
                  transfer.status === 'ACCEPTED' &&
                  $currentUserCan($permissions.PERM_EDIT_ESTABLISHMENT_TRANSFERS)
                "
                class="mr-2"
                type="warning"
                icon
                size="sm"
                :disabled="loading"
                @click="openChangeAcceptedDateModal"
              >
                <i class="fas fa-calendar-alt"></i>
                {{ $t("ESTABLISHMENT_TRANSFERS.CHANGE_ACCEPTED_DATE") }}
              </base-button>
            </h3>
          </div>
        </div>
      </template>

      <modal
        :show.sync="showChangeAcceptedDateModal"
        modal-classes="modal-secondary"
        size="sm"
      >
        <template slot="header">
          <h5 class="modal-title">
            {{ $t("ESTABLISHMENT_TRANSFERS.CHANGE_ACCEPTED_DATE") }}
          </h5>
        </template>
        <form @submit.prevent="">
          <p class="text-muted">
            {{ $t("ESTABLISHMENT_TRANSFERS.CHANGE_ACCEPTED_DATE_HELP") }}
          </p>
          <base-input :label="$t('ESTABLISHMENT_TRANSFERS.ACCEPTED_DATE')">
            <flat-picker
              v-model="newAcceptedDate"
              :config="{
                allowInput: true,
                locale: $flatPickrLocale(),
              }"
              class="form-control datepicker"
            />
          </base-input>
        </form>
        <template slot="footer">
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading || !newAcceptedDate"
            @click="changeAcceptedDate"
          >
            <span v-if="loading" class="btn-inner--icon">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
            {{ $t("COMMON.UPDATE") }}
          </button>
          <button
            type="button"
            class="btn btn-link ml-auto"
            :disabled="loading"
            @click="showChangeAcceptedDateModal = false"
          >
            {{ $t("COMMON.CLOSE") }}
          </button>
        </template>
      </modal>

      <div
        class="col-12 justify-content-center justify-content-sm-between flex-wrap tabs-wrapper"
      >
        <tabs
          fill
          class="flex-column flex-md-row"
          tabNavWrapperClasses="nav-wrapper"
          tabNavClasses="nav nav-pills nav-fill"
          value="details"
        >
          <card shadow>
            <tab-pane title="global" id="1" :active="true">
              <span slot="title">
                <i class="ni ni-cloud-upload-96" />
                {{ $t("COMMON.GLOBAL") }}
              </span>
              <establishment-transfer-view-global :transfer="transfer" />
            </tab-pane>

            <tab-pane title="details" id="2" :active="false">
              <span slot="title">
                <i class="ni ni-bullet-list-67" />
                {{ $t("COMMON.DETAILS") }}
              </span>
              <establishment-transfer-items-table
                :transfer="transfer"
                @onSubmitTransferItem="handleItemSubmit"
                @onDeleteTransferItem="handleItemDelete"
                @onTransferUpdated="refreshTransfer"
              />
              <div class="row mb-5" v-if="transfer.pricing">
                <div class="col-6"></div>
                <div class="col-6">
                  <dl class="row mb-0">
                    <dt class="col-sm-6">{{ $t("COMMON.SUBTOTAL") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency(transfer.pricing.subtotal || 0) }}
                    </dd>
                  </dl>

                  <dl class="row mb-0" v-if="transfer.pricing.discounts?.amount">
                    <dt class="col-sm-6">{{ $t("COMMON.DISCOUNTS") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency((transfer.pricing.discounts.amount || 0) * -1) }}
                    </dd>
                  </dl>

                  <dl class="row mb-0" v-if="transfer.pricing.taxes?.total">
                    <dt class="col-sm-6">{{ $t("COMMON.TAXES") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency(transfer.pricing.taxes.total || 0) }}
                    </dd>
                    <div class="col-12">
                      <dl
                        class="row mb-0 px-0 mx-0"
                        v-for="(tax, index) in transfer.pricing.taxes.taxes"
                        :key="index"
                      >
                        <dt class="col-sm-6 pl-5 text-muted">{{ tax.label }}</dt>
                        <dd class="col-sm-6 text-right text-muted px-0">
                          {{ $formatCurrency(tax.tax_amount || 0) }}
                        </dd>
                      </dl>
                    </div>
                  </dl>

                  <dl class="row mb-0">
                    <dt class="col-sm-6">{{ $t("COMMON.TOTAL") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency(transfer.pricing.total || 0) }}
                    </dd>
                  </dl>
                </div>
              </div>
            </tab-pane>
          </card>
        </tabs>
      </div>
    </card>
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import defaultTransfer from "./defaultEstablishmentTransfer";
import EstablishmentTransferItemsTable from "./partials/EstablishmentTransferItemsTable.vue";
import EstablishmentTransferViewGlobal from "./partials/EstablishmentTransferViewGlobal.vue";
import defaultTransferItem from "./defaultEstablishmentTransferItem";
import { Tabs, TabPane } from "@/components";
import { openPdfBlob } from "@/helpers/downloadPdf";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "ViewEstablishmentTransferPage",
  components: {
    EstablishmentTransferItemsTable,
    EstablishmentTransferViewGlobal,
    Tabs,
    TabPane,
    flatPicker,
  },
  data() {
    return {
      transfer: cloneDeep(defaultTransfer),
      loading: false,
      transferItem: cloneDeep(defaultTransferItem),
      showChangeAcceptedDateModal: false,
      newAcceptedDate: null,
    };
  },
  created() {
    this.getTransfer();
    if (!this.$store.getters["profile/me"]) {
      this.$store.dispatch("profile/me");
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters["profile/me"];
    },
    isSubmitter() {
      if (!this.currentUser || !this.transfer?.submittedBy) {
        return false;
      }
      return this.currentUser.id === this.transfer.submittedBy.id;
    },
    /**
     * A submitted transfer can be canceled by anyone who sees it; an accepted one
     * is already counted in the weekly reports, so it needs its own permission.
     */
    canCancel() {
      if (this.transfer.status === "SUBMITTED") {
        return true;
      }
      return (
        this.transfer.status === "ACCEPTED" &&
        this.$currentUserCan(
          this.$permissions.PERM_CANCEL_ACCEPTED_ESTABLISHMENT_TRANSFERS
        )
      );
    },
  },
  methods: {
    async getTransfer() {
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentTransfers/get", {
          id: this.$route.params.id,
        });
        this.transfer = this.$store.getters["establishmentTransfers/transfer"];
      } finally {
        this.loading = false;
      }
    },
    async refreshTransfer() {
      await this.getTransfer();
    },
    async validate() {
      if (!this.transfer.items || !this.transfer.items.length) {
        this.$notify({
          type: "danger",
          message: this.$t("ESTABLISHMENT_TRANSFERS.ADD_ITEMS_FIRST"),
        });
        return;
      }
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentTransfers/validate", {
          id: this.transfer.id,
        });
        await this.getTransfer();
      } finally {
        this.loading = false;
      }
    },
    async accept() {
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentTransfers/accept", {
          id: this.transfer.id,
        });
        await this.getTransfer();
      } finally {
        this.loading = false;
      }
    },
    openChangeAcceptedDateModal() {
      this.newAcceptedDate = this.transfer.accepted_at
        ? this.transfer.accepted_at.slice(0, 10)
        : null;
      this.showChangeAcceptedDateModal = true;
    },
    async changeAcceptedDate() {
      this.loading = true;
      try {
        await this.$store.dispatch(
          "establishmentTransfers/changeAcceptedDate",
          {
            id: this.transfer.id,
            accepted_at: this.newAcceptedDate,
          }
        );
        this.showChangeAcceptedDateModal = false;
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENT_TRANSFERS.ACCEPTED_DATE_UPDATED"),
        });
        await this.getTransfer();
      } catch (error) {
        this.$showRequestError(error);
      } finally {
        this.loading = false;
      }
    },
    async reject() {
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentTransfers/reject", {
          id: this.transfer.id,
        });
        await this.getTransfer();
      } finally {
        this.loading = false;
      }
    },
    async cancel() {
      if (this.transfer.status === "ACCEPTED") {
        const confirmation = await swal.fire({
          title: this.$t("ESTABLISHMENT_TRANSFERS.CANCEL_ACCEPTED_TRANSFER"),
          text: this.$t("ESTABLISHMENT_TRANSFERS.CANCEL_ACCEPTED_TRANSFER_HELP"),
          type: "warning",
          buttonsStyling: false,
          showCancelButton: true,
          confirmButtonText: this.$t("COMMON.YES"),
          cancelButtonText: this.$t("COMMON.NO"),
          confirmButtonClass: "btn btn-danger",
          cancelButtonClass: "btn btn-warning",
        });

        if (!confirmation.value) return;
      }

      this.loading = true;
      try {
        await this.$store.dispatch("establishmentTransfers/cancel", {
          id: this.transfer.id,
        });
        await this.getTransfer();
      } finally {
        this.loading = false;
      }
    },
    async print() {
      swal.fire({
        title: this.$t("COMMON.LOADING"),
        onBeforeOpen: () => {
          swal.showLoading();
        },
      });
      try {
        const response = await this.$store.dispatch(
          "establishmentTransfers/print",
          { id: this.transfer.id }
        );
        openPdfBlob(
          response,
          `${this.transfer.seq_number || this.transfer.code}.pdf`
        );
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        swal.close();
      }
    },
    async handleItemSubmit(itemData) {
      if (!itemData.id) {
        await this.addTransferItem(itemData);
      } else {
        await this.updateTransferItem(itemData);
      }
      await this.refreshTransfer();
    },
    async addTransferItem(itemData) {
      const payload = {
        ...itemData,
        transfer: {
          type: "establishment-transfers",
          id: this.transfer.id,
        },
      };
      await this.$store.dispatch("establishmentTransferItems/add", payload);
    },
    async updateTransferItem(itemData) {
      const payload = {
        ...itemData,
        transfer: {
          type: "establishment-transfers",
          id: this.transfer.id,
        },
      };
      await this.$store.dispatch("establishmentTransferItems/update", payload);
    },
    async handleItemDelete(id) {
      await this.$store.dispatch("establishmentTransferItems/destroy", id);
      await this.refreshTransfer();
    },
    async deleteTransfer() {
      const confirmation = await swal.fire({
        title: this.$t("ESTABLISHMENT_TRANSFERS.DELETE_ESTABLISHMENT_TRANSFER"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (!confirmation.value) return;

      this.loading = true;
      try {
        await this.$store.dispatch("establishmentTransfers/destroy", {
          id: this.transfer.id,
        });
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENT_TRANSFERS.DELETE_ESTABLISHMENT_TRANSFER"),
        });
        this.goBack();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },
    goToEdit() {
      this.$router.push({
        name: "Edit EstablishmentTransfer",
        params: { id: this.transfer.id },
      });
    },
    goBack() {
      this.$router.push({ name: "List EstablishmentTransfers" });
    },
  },
};
</script>
